import { useMemo } from "react";
import { format, parseISO, subDays } from "date-fns";

import {
  SyncReconciliationStatus,
  useGetSyncReconciliationDataQuery,
  type GetSyncReconciliationDataQuery,
} from "src/graphql";

export type SyncReconciliationPoint =
  GetSyncReconciliationDataQuery["getSyncReconciliationData"][number];

export interface SyncReconciliationTotals {
  sent: number;
  droppedAtSync: number;
  failedAtSync: number;
  unverified: number;
}

type ObservedBucket = Exclude<keyof SyncReconciliationTotals, "unverified">;

const EMPTY_TOTALS: SyncReconciliationTotals = {
  sent: 0,
  droppedAtSync: 0,
  failedAtSync: 0,
  unverified: 0,
};

/** An empty response means no cache, an empty cache, or no recommendations yet,
 * so this must not assert any one cause. */
export const NO_SYNC_RECONCILIATION_DATA_MESSAGE =
  "No sync delivery data yet. Counts require warehouse sync logs on the flow's source, and appear after the next agent run.";

export const SYNC_RECONCILIATION_BUCKETS: {
  key: keyof SyncReconciliationTotals;
  label: string;
  description: string;
  color: string;
  emphasizeWhenNonZero: boolean;
}[] = [
  {
    key: "sent",
    label: "Sent",
    description: "Entered a sync and the destination accepted the row.",
    color: "success.base",
    emphasizeWhenNonZero: false,
  },
  {
    key: "droppedAtSync",
    label: "Dropped at sync",
    description:
      "Marked ready to send but never entered a sync, on a day the sync logs do cover.",
    color: "warning.base",
    emphasizeWhenNonZero: true,
  },
  {
    key: "failedAtSync",
    label: "Failed at sync",
    description: "The sync attempted the row but the destination rejected it.",
    color: "danger.base",
    emphasizeWhenNonZero: true,
  },
  {
    key: "unverified",
    label: "Unverified",
    description:
      "Marked ready to send on a day with no warehouse sync log for this message, so whether it was sent cannot be determined. Enable warehouse sync logs on the flow's source to reconcile these.",
    color: "text.secondary",
    emphasizeWhenNonZero: false,
  },
];

/** Null for statuses that never enter a sync. */
function bucketForStatus(
  status: SyncReconciliationStatus,
): ObservedBucket | null {
  switch (status) {
    case SyncReconciliationStatus.InSyncSucceeded:
      return "sent";
    case SyncReconciliationStatus.NeverSynced:
      return "droppedAtSync";
    case SyncReconciliationStatus.InSyncFailed:
      return "failedAtSync";
    case SyncReconciliationStatus.Holdout:
    case SyncReconciliationStatus.CustomerManaged:
      return null;
    default: {
      const _exhaustive: never = status;
      return null;
    }
  }
}

interface CountedPoint {
  sendDate: string;
  messageId: string;
  bucket: ObservedBucket;
  rowCount: number;
}

function toCountedPoint(point: SyncReconciliationPoint): CountedPoint | null {
  if (point.messageId == null) return null;
  const bucket = bucketForStatus(point.status);
  if (bucket == null) return null;
  return {
    sendDate: point.sendDate,
    messageId: point.messageId,
    bucket,
    rowCount: point.rowCount,
  };
}

function isCounted(point: SyncReconciliationPoint): boolean {
  return toCountedPoint(point) != null;
}

function coverageKey(point: CountedPoint): string {
  return `${point.messageId}\0${point.sendDate}`;
}

/** Exclusive lower bound; prefers `anchorDate` over the latest counted date. */
export function getWindowCutoffDate({
  points,
  windowDays,
  anchorDate,
}: {
  points: SyncReconciliationPoint[];
  windowDays: number | undefined;
  anchorDate?: string | null;
}): string | null {
  if (windowDays == null) return null;

  let latest: string | null = anchorDate ?? null;
  if (latest == null) {
    for (const point of points) {
      if (!isCounted(point)) continue;
      if (latest == null || point.sendDate > latest) latest = point.sendDate;
    }
  }
  if (latest == null) return null;

  return format(subDays(parseISO(latest), windowDays), "yyyy-MM-dd");
}

/** A message that dropped every row on a day reads as unverified, which
 * undercounts drops rather than inventing them. */
export function aggregateReconciliationByMessage({
  points,
  cutoffDate,
}: {
  points: SyncReconciliationPoint[];
  cutoffDate: string | null;
}): Map<string, SyncReconciliationTotals> {
  const counted: CountedPoint[] = [];
  for (const point of points) {
    if (cutoffDate != null && point.sendDate <= cutoffDate) continue;
    const countedPoint = toCountedPoint(point);
    if (countedPoint != null) counted.push(countedPoint);
  }

  const covered = new Set<string>();
  for (const point of counted) {
    if (point.bucket !== "droppedAtSync") covered.add(coverageKey(point));
  }

  const byMessage = new Map<string, SyncReconciliationTotals>();
  for (const point of counted) {
    const bucket =
      point.bucket === "droppedAtSync" && !covered.has(coverageKey(point))
        ? "unverified"
        : point.bucket;

    const totals = byMessage.get(point.messageId) ?? { ...EMPTY_TOTALS };
    totals[bucket] += point.rowCount;
    byMessage.set(point.messageId, totals);
  }

  return byMessage;
}

export function rollUpByChannel({
  byMessage,
  channelIdByMessageId,
}: {
  byMessage: Map<string, SyncReconciliationTotals>;
  channelIdByMessageId: Map<string, string>;
}): Map<string, SyncReconciliationTotals> {
  const byChannel = new Map<string, SyncReconciliationTotals>();

  for (const [messageId, totals] of byMessage) {
    const channelId = channelIdByMessageId.get(messageId);
    if (channelId == null) continue;

    const existing = byChannel.get(channelId) ?? { ...EMPTY_TOTALS };
    byChannel.set(channelId, sumTotals([existing, totals]));
  }

  return byChannel;
}

export function sumTotals(
  list: SyncReconciliationTotals[],
): SyncReconciliationTotals {
  return list.reduce<SyncReconciliationTotals>(
    (acc, totals) => ({
      sent: acc.sent + totals.sent,
      droppedAtSync: acc.droppedAtSync + totals.droppedAtSync,
      failedAtSync: acc.failedAtSync + totals.failedAtSync,
      unverified: acc.unverified + totals.unverified,
    }),
    { ...EMPTY_TOTALS },
  );
}

export function totalRows(totals: SyncReconciliationTotals): number {
  return (
    totals.sent + totals.droppedAtSync + totals.failedAtSync + totals.unverified
  );
}

export function useSyncReconciliationByMessage({
  flowId,
  windowDays,
  anchorDate,
}: {
  flowId: string;
  windowDays: number | undefined;
  anchorDate: string | null;
}): {
  byMessage: Map<string, SyncReconciliationTotals>;
  hasData: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useGetSyncReconciliationDataQuery(
    { flowId },
    { enabled: Boolean(flowId) },
  );

  const points = data?.getSyncReconciliationData;

  const byMessage = useMemo(() => {
    const resolved = points ?? [];
    return aggregateReconciliationByMessage({
      points: resolved,
      cutoffDate: getWindowCutoffDate({
        points: resolved,
        windowDays,
        anchorDate,
      }),
    });
  }, [points, windowDays, anchorDate]);

  return { byMessage, hasData: (points?.length ?? 0) > 0, isLoading };
}
